// External Dependencies
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtModule } from '@auth0/angular-jwt';
import { NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MissingTranslationHandler, TranslateModule } from '@ngx-translate/core';
import { QRCodeModule } from 'angularx-qrcode';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { ToastrModule } from 'ngx-toastr';
// Routing & Configuration
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
// Feature Modules
import { ClubModule } from './modules/club/club.module';
import { EventsModule } from './modules/events/events.module';
import { FantasyModule } from './modules/fantasy/fantasy.module';
import { LoyaltyModule } from './modules/loyalty/loyalty.module';
import { PurchaseModule } from './modules/purchase/purchase.module';
import { UserModule } from './modules/user/user.module';
import { VouchersModule } from './modules/vouchers/vouchers.module';
import { SharedModule } from './shared/shared.module';
import { WalletCustomFieldsInterceptor } from './shared/interceptors/wallet-custom-fields.interceptor';

// Utilities
import { MomentDateFormatter } from './shared/utilities/moment-date-formatter';
// @fgb/core
import { FGBMissingTranslationHandler, PopulationGuard, ProductCodeGuard, jwtTokenGetter, provideAppConfig } from '@fgb/core';

// Component Library
import { ContactUsModule } from '@fgb/portal-component-library/src/lib/club/contact-us';
import { EarnPointsModule } from '@fgb/portal-component-library/src/lib/club/earn-points';
import { FaqSectionModule } from '@fgb/portal-component-library/src/lib/club/faq-section';
import { TermsAndConditionsModule } from '@fgb/portal-component-library/src/lib/club/terms-and-conditions';
import { DirectivesModule } from '@fgb/portal-component-library/src/lib/directives';
import { CardTokenListModule } from '@fgb/portal-component-library/src/lib/ecash/card-token-list';
import { EcashDescriptionModule } from '@fgb/portal-component-library/src/lib/ecash/ecash-description';
import { AuctionDetailsModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-details';
import { AuctionListModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-list';
import { AuctionPanelModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-panel';
import {
  BadgeAchievedModule,
  BadgeFeaturedListModule,
  BadgeListItemModule,
  BadgeOverviewListModule,
} from '@fgb/portal-component-library/src/lib/loyalty/badges';
import { FeaturedRewardsModule } from '@fgb/portal-component-library/src/lib/loyalty/featured-rewards';
import { FeedModule } from '@fgb/portal-component-library/src/lib/feeds';
import { LottosV2Module } from '@fgb/portal-component-library/src/lib/loyalty/lottos/lottos-v2';
import { MarketplaceDetailsModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-details';
import { MarketplaceListModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list';
import { MarketplaceListItemModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list-item';
import { CountryAndRegionsModule } from '@fgb/portal-component-library/src/lib/shared/country-and-regions';
import { ErrorsModule } from '@fgb/portal-component-library/src/lib/shared/errors';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';
import { ShortLongDescriptionModule } from '@fgb/portal-component-library/src/lib/shared/short-long-description';
import { AddressBookModule } from '@fgb/portal-component-library/src/lib/user/address-book';

// General Pages
import { AppComponent } from './app.component';
import { HomePageBannerComponent } from './pages/home-page/home-page-banner.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';

// Loyalty Pages
import { AuctionDetailPageComponent } from './pages/auction-detail-page/auction-detail-page.component';
import { AuctionsPageComponent } from './pages/auctions-page/auctions-page.component';
import { EventsPageComponent } from './pages/events-page/events-page.component';
import { LottoDetailPageComponent } from './pages/lotto-detail-page/lotto-detail-page.component';
import { LottoWalletDetailsPageComponent } from './pages/lotto-wallet-details-page/lotto-wallet-details-page.component';
import { LottosPageComponent } from './pages/lottos-page/lottos-page.component';
import { LottosSuccessPageComponent } from './pages/lottos-page/lottos-success-page/lottos-success-page.component';
import { MarketplaceDetailPageComponent } from './pages/marketplace-detail-page/marketplace-detail-page.component';
import { MarketplacePageComponent } from './pages/marketplace-page/marketplace-page.component';
import { MarketplacePurchaseSuccessPageComponent } from './pages/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-page.component';
import { QuizPageComponent } from './pages/quiz-page/quiz-page.component';
import { RewardsLayoutComponent } from './pages/rewards-layout/rewards-layout.component';
import { RewardsPageComponent } from './pages/rewards-page/rewards-page.component';
import { WalletPageComponent } from './pages/wallet-page/wallet-page.component';
// Polls, Surveys & Trivia (Games)
import { FantasyGamesPageComponent } from './pages/fantasy-games-page/fantasy-games-page.component';
import { FantasyPlayerSelectPageComponent } from './pages/fantasy-player-select-page/fantasy-player-select-page.component';
import { GamesLayoutComponent } from './pages/games-layout/games-layout.component';
import { GamesPageComponent } from './pages/games-page/games-page.component';
import { PollsPageComponent } from './pages/polls-page/polls-page.component';
import { PredictorPageComponent } from './pages/predictor-page/predictor-page.component';
import { PredictorLayoutPageComponent } from './pages/predictor-page/predictor-layout-page/predictor-layout-page.component';
import { PredictorLeaderboardComponent } from './pages/predictor-page/predictor-leaderboard/predictor-leaderboard.component';
// Account Pages
import { AccountDetailsPageComponent } from './pages/account-details-page/account-details-page.component';
import { ActivityPageComponent } from './pages/activity-page/activity-page.component';
// Ecash Pages
import { EcashHistoryPageComponent } from './pages/ecash-history/ecash-history.component';
import { EcashFailurePageComponent } from './pages/ecash-page/ecash-failure-page/ecash-failure-page.component';
import { EcashPageComponent } from './pages/ecash-page/ecash-page.component';
import { EcashResultPageComponent } from './pages/ecash-page/ecash-result-page/ecash-result-page.component';
import { EcashSuccessPageComponent } from './pages/ecash-page/ecash-success-page/ecash-success-page.component';
// Portal Products
import { PortalProductFailurePageComponent } from './pages/portal-products-pages/portal-product-failure-page/portal-product-failure-page.component';
import { PortalProductResultsPageComponent } from './pages/portal-products-pages/portal-product-results-page/portal-product-results-page.component';
import { PortalProductSuccessPageComponent } from './pages/portal-products-pages/portal-product-success-page/portal-product-success-page.component';
// Benefits
import { BenefitModule } from './modules/benefit/benefit.module';
import { BenefitsPageComponent } from './pages/benefits-page/benefits-page.component';
// External Cash Transfer
import { CardPageComponent } from './pages/card-page/card-page.component';
// Club Pages
import { LayoutModule } from './modules/layouts/layout.module';
import { EarnPageComponent } from './pages/earn-page/earn-page.component';
import { GuardianPageComponent } from './pages/guardian-page/guardian-page.component';

import { RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CardLinkModule } from '@fgb/portal-component-library/src/lib/cardlinking';
import { AnnouncementsModule } from '@fgb/portal-component-library/src/lib/club/announcements';
import { NotificationModule } from '@fgb/portal-component-library/src/lib/club/notification';
import { OfferSearchBarModule } from '@fgb/portal-component-library/src/lib/club/offer-search-bar';
import { GamesModule } from '@fgb/portal-component-library/src/lib/games';
import { AddPaymentCardDetailModule } from '@fgb/portal-component-library/src/lib/loyalty/add-payment-card-detail';
import { CartItemModule } from '@fgb/portal-component-library/src/lib/loyalty/cart-item';
import { MixedLoyaltyListModule } from '@fgb/portal-component-library/src/lib/loyalty/mixed-loyalty';
import { ScorecardModule } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import { WalletModule } from '@fgb/portal-component-library/src/lib/loyalty/wallet';
import { RegistrationNavigationModule } from '@fgb/portal-component-library/src/lib/registration';
import { CountdownModule } from '@fgb/portal-component-library/src/lib/shared/countdown';
import { SelectCountryModule } from '@fgb/portal-component-library/src/lib/shared/select-country';
import { SelectRegionModule } from '@fgb/portal-component-library/src/lib/shared/select-region';
import { SharedSearchBarModule } from '@fgb/portal-component-library/src/lib/shared/shared-search-bar';
import { TicketTransferModule } from '@fgb/portal-component-library/src/lib/ticket-transfer';
import { ClubSelectorModule } from '@fgb/portal-component-library/src/lib/user/club-selector';
import { EditQuestionsModule } from '@fgb/portal-component-library/src/lib/user/edit-questions';
import { RegistrationAccountVerificationComponent } from 'src/app/modules/registration/pages/registration-account-verification/registration-account-verification.component';
import { RegistrationBenefitOptionsComponent } from 'src/app/modules/registration/pages/registration-benefits/registration-benefits-options.component';
import { RegistrationSuccessPageComponent } from 'src/app/modules/registration/pages/registration-success-page/registration-success-page.component';
import { RegistrationPaymentComponent } from 'src/app/modules/registration/pages/steps/registration-payment/registration-payment.component';
import { AccountVerifiedPageComponent } from 'src/app/pages/account-activation/account-verified-page/account-verified-page.component';
import { ActivateAccountPageComponent } from 'src/app/pages/account-activation/activate-account-page/activate-account-page.component';
import { WelcomeComponent } from './modules/club/components/welcome/welcome.component';
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';
import { AuctionWalletDetailsPageComponent } from './pages/auction-wallet-details-page/auction-wallet-details-page.component';
import { BadgesPageComponent } from './pages/badges-page/badges-page.component';
import { CampaignDetailsPageComponent } from './pages/campaign-details-page/campaign-details-page.component';
import { CartCheckoutPagePortalProductsComponent } from './pages/cart-checkout-page-portal-products/cart-checkout-page-portal-products.component';
import { CartCheckoutPageComponent } from './pages/cart-checkout-page/cart-checkout-page.component';
import { CartCheckoutResultsPageComponent } from './pages/cart-checkout-page/cart-checkout-results-page/cart-checkout-results-page.component';
import { CartCheckoutSuccessPageComponent } from './pages/cart-checkout-page/cart-checkout-success-page/cart-checkout-success-page.component';
import { ChallengesPageComponent } from './pages/challenges-page/challenges-page.component';
import { FantasyLeaderboardPageComponent } from './pages/fantasy-leaderboard-page/fantasy-leaderboard-page.component';
import { LottosTermsPageComponent } from './pages/lottos-terms-page/lottos-terms-page.component';
import { MarketplaceTermsPageComponent } from './pages/marketplace-terms-page/marketplace-terms-page.component';
import { MarketplaceWalletPageComponent } from './pages/marketplace-wallet-page/marketplace-wallet-page.component';
import { OffersPageComponent } from './pages/offers-page/offers-page.component';
import { PaymentPageComponent } from './pages/payment-page/payment-page.component';
import { ProductsPageComponent } from './pages/products-page/products-page.component';
import { RegistrationMarketingPageComponent } from './pages/registration-marketing-page/registration-marketing-page.component';
import { FilterModalModule } from './pages/rewards-page/filter-modal';
import { TrainingZonePageComponent } from './pages/training-zone-page/training-zone-page.component';
import { WalletClaimedPageComponent } from './pages/wallet-claimed-page/wallet-claimed-page.component';
import { WorksheetPageComponent } from './pages/worksheet-page/worksheet-page.component';
import { RegistrationTermsAndConditionsComponent } from './modules/registration/pages/steps/registration-terms-and-conditions/registration-terms-and-conditions.component';
import { ArcadePageComponent } from './pages/arcade-page/arcade-page.component';
import { STREAMAMG_CONFIG } from './shared/components/providers/steamAMG/stream-amg-player/stream-amg-config';
import { buildPaymentTemplates } from '@fgb/portal-component-library/src/lib/shared/payment';
import { QuizLeaderboardComponent } from './pages/quiz-page/quiz-leaderboard/quiz-leaderboard.component';
import { QuizLayoutPageComponent } from './pages/quiz-page/quiz-layout-page/quiz-layout-page.component';
import { BrandPageComponent } from './pages/brand-page/brand-page.component';
import { BrandListComponent } from './pages/brand-page/components/brand-list/brand-list.component';
import { BrandDetailPageComponent } from './pages/brand-page/brand-detail-page/brand-detail-page.component';

@NgModule({
  declarations: [
    // General Pages
    AppComponent,
    MasterPageComponent,
    HomePageComponent,
    HomePageBannerComponent,
    LoginPageComponent,
    MaintenancePageComponent,
    NotFoundPageComponent,
    // Loyalty Pages
    RewardsLayoutComponent,
    RewardsPageComponent,
    AuctionsPageComponent,
    LottosPageComponent,
    MarketplacePageComponent,
    EventsPageComponent,
    WalletPageComponent,
    AuctionDetailPageComponent,
    LottoDetailPageComponent,
    MarketplaceDetailPageComponent,
    LottosSuccessPageComponent,
    MarketplacePurchaseSuccessPageComponent,
    LottoWalletDetailsPageComponent,
    AuctionWalletDetailsPageComponent,
    // Polls, Surveys & Trivia Pages (Games)
    GamesLayoutComponent,
    GamesPageComponent,
    PollsPageComponent,
    FantasyGamesPageComponent,
    FantasyPlayerSelectPageComponent,
    PredictorPageComponent,
    PredictorLayoutPageComponent,
    PredictorLeaderboardComponent,
    // Account Pages
    AccountDetailsPageComponent,
    ActivityPageComponent,
    ActivateAccountPageComponent,
    AccountVerifiedPageComponent,
    // Ecash Pages
    EcashPageComponent,
    EcashResultPageComponent,
    EcashSuccessPageComponent,
    EcashFailurePageComponent,
    EcashHistoryPageComponent,
    PaymentPageComponent,
    PortalProductResultsPageComponent,
    PortalProductSuccessPageComponent,
    PortalProductFailurePageComponent,
    // Guardians Pages
    GuardianPageComponent,
    // External Cash Transfers
    CardPageComponent,
    // Club Pages
    EarnPageComponent,
    BenefitsPageComponent,
    QuizPageComponent,
    LottosTermsPageComponent,
    MarketplaceTermsPageComponent,
    WorksheetPageComponent,
    TrainingZonePageComponent,
    FantasyLeaderboardPageComponent,
    CartCheckoutPagePortalProductsComponent,
    CartCheckoutPageComponent,
    CartCheckoutSuccessPageComponent,
    CartCheckoutResultsPageComponent,
    ProductsPageComponent,
    BadgesPageComponent,
    CampaignDetailsPageComponent,
    WelcomeComponent,
    // Registration
    RegistrationLayoutPageComponent,
    WalletClaimedPageComponent,
    MarketplaceWalletPageComponent,
    ChallengesPageComponent,
    OffersPageComponent,
    RegistrationPaymentComponent,
    RegistrationSuccessPageComponent,
    RegistrationMarketingPageComponent,
    RegistrationBenefitOptionsComponent,
    RegistrationTermsAndConditionsComponent,
    ArcadePageComponent,
    QuizLeaderboardComponent,
    QuizLayoutPageComponent,
    BrandPageComponent,
    BrandListComponent,
    BrandDetailPageComponent,
  ],
  imports: [
    // External Modules
    RouterModule,
    CarouselModule,
    AppRoutingModule,
    FormsModule,
    NgbModule,
    BrowserModule,
    HttpClientModule,
    ReactiveFormsModule,
    InlineSVGModule.forRoot(),
    QRCodeModule,
    BrowserAnimationsModule,
    EarnPointsModule,
    FaqSectionModule,
    TermsAndConditionsModule,
    AuctionDetailsModule,
    AuctionPanelModule,
    AuctionListModule,
    LottosV2Module,
    ScorecardModule,
    BadgeListItemModule,
    BadgeOverviewListModule,
    BadgeFeaturedListModule,
    BadgeAchievedModule,
    MarketplaceDetailsModule,
    ShortLongDescriptionModule,
    MarketplaceListModule,
    MarketplaceListItemModule,
    MixedLoyaltyListModule,
    LoadingModule,
    CountryAndRegionsModule,
    EcashDescriptionModule,
    CardTokenListModule,
    CountdownModule,
    ErrorsModule,
    EditQuestionsModule,
    FeaturedRewardsModule,
    FeedModule,
    AddressBookModule,
    WalletModule,
    NotificationModule,
    ContactUsModule,
    AddPaymentCardDetailModule,
    FilterModalModule,
    TicketTransferModule,
    ToastrModule.forRoot({
      maxOpened: 5,
    }),
    JwtModule.forRoot({
      config: {
        allowedDomains: ['http://localhost/portal2/api'],
        tokenGetter: jwtTokenGetter,
      },
    }),
    TranslateModule.forRoot({
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: FGBMissingTranslationHandler,
      },
    }),
    // Feature Modules
    LayoutModule,
    PurchaseModule,
    EventsModule,
    ClubModule,
    PurchaseModule,
    VouchersModule,
    UserModule,
    SharedModule,
    FantasyModule,
    LoyaltyModule,
    BenefitModule,
    DirectivesModule,
    CardLinkModule,
    AnnouncementsModule,
    OfferSearchBarModule,
    SharedSearchBarModule,
    CartItemModule,
    ClubSelectorModule,
    GamesModule,
    ServiceWorkerModule.register('fgb-service-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    SelectCountryModule,
    SelectRegionModule,
    RegistrationAccountVerificationComponent,
    RegistrationNavigationModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: WalletCustomFieldsInterceptor, multi: true },
    provideAppConfig({
      dateOptions: {
        LongDateFormat: 'DD/MM/yyyy h:mma',
        NgbDateFormat: 'DD/MM/yyyy',
        ShortDateFormat: 'D MMM, YYYY',
      },

      paymentTemplates: buildPaymentTemplates(),
    }),
    PopulationGuard,
    ProductCodeGuard,
    { provide: NgbDateParserFormatter, useClass: MomentDateFormatter },
    {
      provide: STREAMAMG_CONFIG,
      useValue: {
        ApiKey: '6WEUmCA4Py4PDNbMbH3tO1dfPymCIQxd6BOAmmw3',
      },
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
